import React from 'react';
import PropTypes from 'prop-types';
import Option from './Option';
import { secondaryAttributesShape } from './optionShape';
import magazineStyle from './MagazineStyle';
import { injectTestId } from '@cvent/nucleus-test-automation';
import findIndex from 'lodash.findindex';

export default class Magazine extends React.Component {
  static propTypes = {
    /** 'options' with duplicate values will be removed */
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.any])
            .isRequired,
          label: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.node,
            PropTypes.func
          ]),
          secondary: secondaryAttributesShape,
          tooltipText: PropTypes.string,
          heading: PropTypes.bool,
          disabled: PropTypes.bool
        }),
        PropTypes.string
      ])
    ).isRequired,
    /** returns 'option' object of newly selected option */
    onUpdate: PropTypes.func.isRequired,
    /** pass down a function that enables Magazine to unmount itself */
    handleClose: PropTypes.func.isRequired,
    /** Carina theme object */
    theme: PropTypes.object.isRequired,
    /** HTML id attribute will be applied as appropriate */
    id: PropTypes.string,
    /** value of 'option' to be displayed */
    selected: PropTypes.string,
    /** Moves focus to Magazine on mount, when true. */
    focusOnMount: PropTypes.bool,
    /** passes prop to options for styling */
    hasError: PropTypes.bool,
    /** pass down testID from parent */
    testID: PropTypes.string,
    /** pass down isRTL from parent */
    isRtl: PropTypes.bool,
    optionStyleObject: PropTypes.object,
    magazineStyleObject: PropTypes.object,
    /** Ref to be applied to magazine outer container */
    forwardedRef: PropTypes.any,
    /** Ref will be focused when alphanumeric key is pressed instead of using internal filtering (for Combobox) */
    refToFocusOnKeyboardInput: PropTypes.any,
    /** For use with combobox: pass key to callback to update inputValue */
    keyPressCallback: PropTypes.func,
    /** References the ID of the trigger button */
    ariaLabelledby: PropTypes.string
  };

  magazineRef = this.props.forwardedRef || React.createRef();

  state = {
    currentFocus: null,
    options: null,
    filterActive: false,
    filterString: ''
  };

  static getDerivedStateFromProps(props, state) {
    return {
      ...state,
      options: Magazine.cleanOptions(props.options)
    };
  }

  static cleanOptions = options =>
    options.reduce(
      // remove options with duplicate values
      (accumulator, option) => {
        const newOption = typeof option === 'string' ? { value: 0 } : option;

        if (accumulator.valueCheck.indexOf(newOption.value) === -1) {
          // if no 'label' property is provided, use 'value'
          if (typeof option === 'string') {
            newOption.label = option;
            newOption.value = option;
          } else {
            if (option.label === undefined) {
              newOption.label = option.value;
            }
          }
          return {
            valueCheck: [...accumulator.valueCheck, option.value],
            noDuplicateOptions: [...accumulator.noDuplicateOptions, newOption]
          };
        }
        return accumulator;
      },
      { valueCheck: [], noDuplicateOptions: [] }
    ).noDuplicateOptions;

  componentDidMount() {
    if (this.props.focusOnMount) {
      // give popper time to render and select direction to avoid window scrolling erratically on focus
      setTimeout(() => {
        if (this.magazineRef.current) this.magazineRef.current.focus();
      }, 100);
    }
    document.addEventListener('click', this.handleOutsideClick);
  }
  componentWillUnmount() {
    // needed to prevent setting state on unmounted component with timeout
    this.willUnmount = true;

    document.removeEventListener('click', this.handleOutsideClick);
  }

  activeDescendant = () => {
    const { id, options } = this.props;
    const { currentFocus } = this.state;

    if (
      !id ||
      typeof currentFocus !== 'number' ||
      !options[currentFocus]?.value
    )
      return;

    return `${id}__${options[currentFocus]?.value}`;
  };

  render() {
    const {
      id,
      ariaLabelledby,
      options,
      magazineStyleObject,
      isRtl,
      testID,
      theme,
      onUpdate,
      handleClose,
      selected,
      focusOnMount,
      hasError,
      optionStyleObject,
      forwardedRef,
      refToFocusOnKeyboardInput,
      keyPressCallback,
      ...propDrop
    } = this.props;
    const { base: baseStyle, rtl: rtlStyle } = magazineStyle(theme);

    return (
      <ul
        {...propDrop}
        id={id}
        aria-labelledby={ariaLabelledby}
        aria-activedescendant={this.activeDescendant()}
        {...injectTestId(testID ? testID + '-magazine' : undefined)}
        css={{
          ...baseStyle,
          ...(isRtl ? rtlStyle : {}),
          ...magazineStyleObject
        }}
        ref={this.magazineRef}
        tabIndex={-1}
        onKeyDown={this.handleKeyDown}
        role="menu"
        onFocus={this.handleWrapperFocus}
        onClick={this.handleOutsideClick}
      >
        {this.buildOptionsElements()}
      </ul>
    );
  }

  handleOutsideClick = ({ target }) => {
    if (
      target !== this.magazineRef.current &&
      !target.contains(this.magazineRef.current)
    ) {
      this.setState({ currentFocus: null }, () => target.focus());
    }
  };

  handleWrapperFocus = event => {
    event.stopPropagation();
    if (this.state.currentFocus === null) {
      const selectedIndex = findIndex(
        this.state.options,
        option => option.value === this.props.selected
      );
      const currentFocus =
        selectedIndex === -1 ? this.getNextFocusable(null, 1) : selectedIndex;
      this.setState({
        currentFocus
      });
    }
  };

  buildOptionsElements = () =>
    this.state.options.map((option, index) => (
      <Option
        id={this.props.id ? `${this.props.id}__${option.value}` : undefined}
        key={index}
        optionIndex={index}
        option={option}
        theme={this.props.theme}
        onUpdate={this.handleSelect}
        focused={this.state.currentFocus === index}
        selected={this.props.selected === option.value}
        setCurrentFocus={this.setCurrentFocus}
        hasError={this.props.hasError}
        isRtl={this.props.isRtl}
        testID={this.props.testID}
        optionStyleObject={this.props.optionStyleObject}
      />
    ));

  handleSelect = (option, optionIndex) => {
    this.setState({ currentFocus: optionIndex }, () => {
      this.props.onUpdate(option);
      this.handleClose();
    });
  };

  handleKeyDown = event => {
    const { key, keyCode } = event;
    switch (true) {
      case key === 'ArrowDown':
        this.handleArrowKeyPress(1, event);
        break;
      case key === 'ArrowUp':
        this.handleArrowKeyPress(-1, event);
        break;
      case key === 'Escape':
        this.handleClose();
        break;
      case key === 'Tab':
        this.handleClose();
        break;
      case key === 'Home':
        this.setState({ currentFocus: this.getNextFocusable(null, 1) });
        break;
      case key === 'End':
        this.setState({
          currentFocus: this.getNextFocusable(this.state.options.length, -1)
        });
        break;
      case keyCode > 47 && keyCode !== 127: // alphanumeric or symbol
        this.handleFilterKey(key);
        break;
      default:
        break;
    }
  };

  handleFilterKey = key => {
    const { refToFocusOnKeyboardInput } = this.props;
    // focus on combobox input if ref is provided
    if (refToFocusOnKeyboardInput) {
      if (refToFocusOnKeyboardInput.current) {
        this.setState({ currentFocus: null }, () =>
          refToFocusOnKeyboardInput.current.focus()
        );
      }
    } else {
      // use dropdown style filtering to focus key input
      this.useInternalKeyboardFiltering(key);
    }
  };

  useInternalKeyboardFiltering = key => {
    // is within add-to-filter time-limit
    if (this.state.filterActive) {
      this.setState(
        { filterString: this.state.filterString + key },
        this.matchOptionsToFilter
      );
    } else {
      // start new filter string
      this.setState(
        { filterActive: true, filterString: key },
        this.matchOptionsToFilter
      );
      setTimeout(
        // add-to-filter time expires
        () =>
          !this.willUnmount &&
          this.setState({ filterActive: false, filterString: '' }),
        3000
      );
    }
  };

  // focus on option that matches typed user input
  matchOptionsToFilter = () => {
    const { options } = this.props;
    const { filterString } = this.state;
    const matchesMiddle = [];

    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      const useLabel = typeof option.label === 'string';
      const useValue = typeof option.value === 'string';
      const stringForFilter =
        (useLabel && option.label) || (useValue && option.value) || '';

      // check beginning of string for match
      if (
        stringForFilter.toLowerCase().startsWith(filterString.toLowerCase())
      ) {
        this.setState({ currentFocus: i });
        break; // focuses on first match at start of string
      } else if (
        // check middle of string for match
        stringForFilter.toLowerCase().includes(filterString.toLowerCase())
      ) {
        matchesMiddle.push(i);
      }
    }
    // no match at beginning, focus on option that matches in middle
    if (matchesMiddle.length > 0) {
      this.setState({ currentFocus: matchesMiddle[0] });
    }
  };
  // skip focus for options that are disabled or headings
  getNextFocusable = (optionIndex, iterateBy) => {
    const optionsLength = this.state.options.length - 1;
    const startTopOrBottom = iterateBy === -1 ? optionsLength : 0;
    const nextOptionIndex =
      optionIndex === null ? startTopOrBottom : optionIndex + iterateBy;

    const option = this.state.options[nextOptionIndex];

    if (option?.disabled || option?.heading) {
      if (
        (iterateBy === 1 && optionIndex < optionsLength) ||
        (iterateBy === -1 && optionIndex > 1)
      ) {
        return this.getNextFocusable(nextOptionIndex, iterateBy);
      }
      return optionIndex;
    }
    return nextOptionIndex;
  };

  // for 'ArrowUp' iterateBy = -1, for 'ArrowDown' iterateBy = 1
  handleArrowKeyPress = (iterateBy, event) => {
    // keep Magazine from scrolling with arrow key press
    event.preventDefault();
    event.stopPropagation();

    const { currentFocus } = this.state;
    const optionsLength = this.state.options.length - 1;

    if (
      (iterateBy === 1 && currentFocus < optionsLength) ||
      (iterateBy === -1 && currentFocus > 0)
    ) {
      this.setState({
        currentFocus: this.getNextFocusable(currentFocus, iterateBy)
      });
    }
  };

  setCurrentFocus = optionIndex => this.setState({ currentFocus: optionIndex });

  handleClose = () => this.props.handleClose && this.props.handleClose();
}
